import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { reset } from '../store/profile.slice';
import { resetIntegrationId } from '../store/zohoIntegrationId.slice';
import { removeAuthToken } from '../utils/auth-token';
import { getProfile, IProfile } from '../api/profile.api';

interface SubscriptionGuardProps {
  children: JSX.Element;
}

export const SubscriptionGuard: FC<SubscriptionGuardProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [profile, setProfile] = useState<IProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch fresh profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const freshProfile = await getProfile();
        setProfile(freshProfile);
      } catch (error) {
        console.error('Error fetching profile in SubscriptionGuard:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Refetch profile data when navigating to subscription routes to ensure fresh subscription info
  useEffect(() => {
    const isSubscriptionRoute = location.pathname === '/settings/subscription' || location.pathname === '/subscription';
    const isSettingsRoute = location.pathname.startsWith('/settings');
    if ((isSubscriptionRoute || isSettingsRoute) && profile) {
      // Refetch to get fresh subscription info
      getProfile()
        .then(freshProfile => {
          setProfile(freshProfile);
        })
        .catch(error => {
          console.error('Error refetching profile in SubscriptionGuard:', error);
        });
    }
  }, [location.pathname]);

  // Handle logout for admin and member when subscription is suspended or profile is banned
  // NOTE: This useEffect MUST come before any early returns (Rules of Hooks)
  useEffect(() => {
    if (!profile) return; // Guard clause - only run when profile exists

    const tenant = profile.tenants?.data?.find(
      (t: any) => t.is_active === true
    );
    const statusValue = profile?.subscription_info?.status_value;
    const isSuspended =
      statusValue === 4 ||
      statusValue === '4' ||
      statusValue === 5 ||
      statusValue === '5' ||
      statusValue === 6 ||
      statusValue === '6';
    const isBanned = Number(profile?.status) === 3;
    const roleValue = profile?.role?.role_value;
    const isAdmin = roleValue === 0;
    const isMember = roleValue === 2;
    const isAllowedPage =
      location.pathname === '/subscription-suspended' ||
      location.pathname === '/subscriptions/deactivate-clients' ||
      location.pathname === '/plans' ||
      location.pathname === '/plans/summary' ||
      location.pathname.startsWith('/login') ||
      location.pathname.startsWith('/register') ||
      location.pathname.startsWith('/i/');

    if ((isSuspended || isBanned) && !isAllowedPage && (isAdmin || isMember)) {
      localStorage.removeItem('email');
      localStorage.removeItem('isNew');
      removeAuthToken();
      sessionStorage.clear();
      dispatch(resetIntegrationId());
      dispatch(reset());
      window.location.href = '/login';
    }
  }, [profile, location.pathname, dispatch]);

  // Show loading state while fetching profile
  if (isLoading) {
    return null; // or a loading spinner
  }

  // If no profile data, let the children render (ProtectedRoute will handle auth)
  if (!profile) {
    return children;
  }

  // Get tenant info from profile
  const tenant = profile.tenants?.data?.find((t: any) => t.is_active === true);

  // Check if subscription_info is null - redirect to plans page
  const hasSubscriptionInfo =
    profile?.subscription_info !== null &&
    profile?.subscription_info !== undefined;

  // Check if user needs to deactivate members
  const showDeactivateMembers =
    profile?.subscription_info?.show_deactivate_members === true;

  // Check if subscription is suspended (status_value === 4, 5, or 6)
  const statusValue = profile?.subscription_info?.status_value;
  const isSuspended =
    statusValue === 4 ||
    statusValue === '4' ||
    statusValue === 5 ||
    statusValue === '5' ||
    statusValue === 6 ||
    statusValue === '6';

  // Check if profile status is BANNED (status === 3)
  const isBanned = Number(profile?.status) === 3;

  // Check user role
  const roleValue = profile?.role?.role_value;
  const isOwner = tenant?.is_owner === true || roleValue === 1;
  const isAdmin = roleValue === 0;
  const isMember = roleValue === 2;

  // Allow access to specific pages
  const isAllowedPage =
    location.pathname === '/subscription-suspended' ||
    location.pathname === '/subscriptions/deactivate-clients' ||
    location.pathname === '/plans' ||
    location.pathname === '/plans/summary' ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/i/');

  // If subscription_info is null and not already on plans page, redirect to plans
  // Exception: allow access to subscription routes if just activated plan (data will refresh)
  const justActivatedPlan = (location.state as any)?.justActivatedPlan === true;
  const isSubscriptionRoute = location.pathname === '/settings/subscription' || location.pathname === '/subscription';
  
  if (!hasSubscriptionInfo && !isAllowedPage && !(justActivatedPlan && isSubscriptionRoute)) {
    return <Navigate to="/plans" replace />;
  }

  // if (isPlansPage && hasSubscriptionInfo) {
  //   return (
  //     <Navigate
  //       to="/appointments?status[0]=upcoming&status[1]=approved"
  //       replace
  //     />
  //   );
  // }

  // If user needs to deactivate members and not already on that page, redirect
  if (showDeactivateMembers) {
    // check the role if member or admin logout and redirect to login page
    if (isAdmin || isMember) {
      localStorage.removeItem('email');
      localStorage.removeItem('isNew');
      removeAuthToken();
      sessionStorage.clear();
      dispatch(resetIntegrationId());
      dispatch(reset());
      window.location.href = '/login';
    } else {
      return <Navigate to="/subscriptions/deactivate-clients" replace />;
    }
  }

  // If subscription is suspended or profile is banned and user is admin or member, don't render (they will be logged out)
  if ((isSuspended || isBanned) && !isAllowedPage && (isAdmin || isMember)) {
    return null;
  }

  // If subscription is suspended and user is owner, redirect to suspension page
  if (isSuspended && !isAllowedPage && isOwner) {
    return <Navigate to="/subscription-suspended" replace />;
  }

  return children;
};
